import type {
  GetServerSideProps,
  GetServerSidePropsContext,
  GetServerSidePropsResult,
  Redirect,
} from 'next';
import { Session, getServerSession } from 'next-auth';
import getConfig from 'next/config';
import { nextAuthOptions } from '../../helpers/nextAuthOptions';
import { ParsedUrlQuery } from 'querystring';
import { getTLD } from '../../helpers/domain';
import { PAGE } from '../../helpers/pages';
import { logger } from '@dist-marketplace-frontend/services';

/**
 * This function will correctly format the Authorization header with
 * @param token the authentication token
 * @returns the formatted Authorization header
 */
const setAuthorizationHeader = (token: string) => {
  return `Bearer ${token}`;
};

export type RedirectResponse = { redirect: Redirect };

const redirect = (destination: string): RedirectResponse => {
  return {
    redirect: {
      destination,
      permanent: false,
    },
  };
};

/**
 * Converts Next.js data URLs back to page routes for proper redirect handling. When Next.js makes
 * client-side navigation requests, it fetches JSON data files instead of navigating to the actual
 * page. This function normalizes those URLs.
 *
 * This is necessary in the case where we have a next/link to a page which requires login. In that
 * case, next/link will send a request to 'some-page.json' instead of 'some-page'. In order to
 * redirect properly through our auth system, we need to normalize the URL so it comes through upon
 * redirect as 'some-page'. This will cause a full-page reload at 'some-page', which is what we
 * want. If we don't do this, the auth system redirects to 'some-page.json', which ends up showing
 * the user a big JSON blob instead of the page.
 *
 * @param url - The request URL that might be a Next.js data URL
 * @returns The normalized page URL
 *
 * @example
 * getPageUrlFromDataUrl('/_next/data/buildId/publish.json') // returns '/publish'
 * getPageUrlFromDataUrl('/_next/data/buildId/index.json') // returns '/'
 * getPageUrlFromDataUrl('/_next/data/buildId/page.json?param=value') // returns '/page?param=value'
 * getPageUrlFromDataUrl('/regular-page') // returns '/regular-page'
 */
const getPageUrlFromDataUrl = (url: string): string => {
  try {
    // We're not using localhost here, but the URL constructor needs a domain if one is not present
    // in the URL. URL a very stable parser which handles a lot of edge cases.
    const urlObj = new URL(url, 'http://localhost');
    const pathname = urlObj.pathname;

    // Handle Next.js data URLs like /_next/data/{buildId}/path.json
    const dataUrlRegex = /\/_next\/data\/[^/]+\/(.+)\.json$/;
    const dataUrlMatch = dataUrlRegex.exec(pathname);
    if (dataUrlMatch) {
      const pagePath = dataUrlMatch[1];
      // Remove trailing /index if present
      const cleanPath = pagePath.replace(/\/?index$/, '');
      return `/${cleanPath}${urlObj.search}`;
    }

    return url;
  } catch (error: unknown) {
    // If URL parsing fails, return the original URL
    logger.error({
      message: 'getPageUrlFromDataUrl parse failure',
      error,
    });
    return url;
  }
};

type AuthGuard = <P extends { [key: string]: any }, Q extends ParsedUrlQuery>(
  cb: (
    context: GetServerSidePropsContext<Q>,
    session?: Session,
  ) => Promise<GetServerSidePropsResult<P>>,
) => GetServerSideProps<P, Q>;

// getServerSideProps function decorator
const authGuard: AuthGuard = (callback) => async (context) => {
  const {
    publicRuntimeConfig: { BASE_URL },
  } = getConfig();
  const tld = getTLD(context.req);
  const baseUrl = `${BASE_URL}${tld}`;

  try {
    const session = await getServerSession(
      context.req,
      context.res,
      nextAuthOptions,
    );

    const isLoggedInKeycloakUser = !!session;

    if (isLoggedInKeycloakUser) {
      const serverSidePropsResult = await callback(context, session);
      return serverSidePropsResult;
    }

    const normalizedUrl = getPageUrlFromDataUrl(context.req.url || '/');
    const redirectUrl = encodeURIComponent(
      `${baseUrl}/signin?callbackUrl=${baseUrl}${normalizedUrl}`,
    );
    return redirect(`/signin?callbackUrl=${redirectUrl}`);
  } catch (error) {
    logger.error({
      error,
      route: PAGE.GSSP_WRAPPER,
      message: 'Failed to get server session',
    });
    return redirect(baseUrl);
  }
};

export { authGuard, setAuthorizationHeader, getPageUrlFromDataUrl };
